import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { Input } from '@/components/ui/input.tsx';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback.ts';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';

type PlantsSearchInputProps = {
  defaultValue?: string;
  placeholder?: string;
  debounceMs?: number;
  onSearch: (value: string) => void;
};

export const PlantsSearchInput = ({
  defaultValue = '',
  placeholder = 'Search by name or setup type...',
  debounceMs = 300,
  onSearch,
}: PlantsSearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [value, setValue] = useState(defaultValue);
  const debouncedOnSearch = useDebouncedCallback(onSearch, debounceMs);

  useEffect(() => {
    if (isDesktop) {
      inputRef.current?.focus();
    }
  }, [isDesktop]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      debouncedOnSearch(event.target.value);
    },
    [debouncedOnSearch]
  );

  const handleClear = useCallback(() => {
    setValue('');
    onSearch('');
    inputRef.current?.focus();
  }, [onSearch]);

  return (
    <div className="relative w-full sm:w-auto">
      <SearchIcon className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
      <Input
        ref={inputRef}
        className="w-full pr-8 pl-7.5 sm:min-w-75"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
      />
      {value && (
        <button
          title="Clear Input"
          type="button"
          className="text-muted-foreground absolute top-0 right-0 cursor-pointer p-2.5"
          onClick={handleClear}
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
};
